// The page's cards as one set: every Card (and every FoldCard with an id) remembers its own open/closed choice per page
// in this browser, and opens when the address's #hash names it. The layout (components/Layout.tsx) wraps each page in
// FoldPageProvider; screens do nothing. (Expand all · Collapse all went in the UX review of 30 Oct 2026: a page that
// needs it has too many cards.)
import { createContext, useContext, useMemo, type ReactNode } from "react";

export interface FoldPageApi {
  /** The page's address (no trailing slash), part of every card's remembered key. */
  path: string;
  /** The address's #hash: a card whose id it names opens. */
  hash: string;
}

const FoldPageContext = createContext<FoldPageApi | null>(null);

/** The page's fold set, or null outside the layout (a test rendering one screen, a public page). */
export function useFoldPage(): FoldPageApi | null {
  return useContext(FoldPageContext);
}

const clean = (path: string) => (path.length > 1 ? path.replace(/\/+$/, "") : path);

/** Where a card's choice is kept: "ogma.fold:<page path>#<card id>". */
export function foldKey(path: string, id: string): string {
  return `ogma.fold:${clean(path)}#${id}`;
}

/**
 * A card's remembered choice. Stored as "1" (open) or "0" (closed), with "w" added when the choice was made while the
 * card had things waiting. A card that is waiting now but was closed when it was not opens again, so nothing new
 * waiting for the owner is hidden by an old choice. Null when nothing is kept or storage is blocked.
 */
export function readFold(key: string, waiting: boolean): boolean | null {
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return null;
    const open = v.startsWith("1");
    if (!open && waiting && !v.endsWith("w")) return null;
    return open;
  } catch {
    return null;
  }
}

export function writeFold(key: string, open: boolean, waiting: boolean): void {
  try {
    window.localStorage.setItem(key, `${open ? "1" : "0"}${waiting ? "w" : ""}`);
  } catch {
    // Private window or blocked storage: the card still works, it just forgets.
  }
}

/** Wraps one page: its address keys each card's remembered choice, and its #hash opens the card it names. */
export function FoldPageProvider({ path, hash = "", children }: { path: string; hash?: string; children: ReactNode }) {
  const page = clean(path);
  const api = useMemo<FoldPageApi>(() => ({ path: page, hash }), [page, hash]);
  return <FoldPageContext.Provider value={api}>{children}</FoldPageContext.Provider>;
}
